import { useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { bootListState, registerListStateProvider } from "../utils/listState";

const scrollPositions = {};

// A cold back (utils/listState.js): the positions saved when this URL was
// left come back once, as soon as each scroller can reach them (its content
// usually arrives after mount). Stops when reached, on the visitor's first
// touch/wheel/key, or after RESTORE_MAX_MS.
const pendingRestore = new Set();
if (bootListState?.scroll) {
  Object.assign(scrollPositions, bootListState.scroll);
  Object.keys(bootListState.scroll).forEach((keyAxis) => pendingRestore.add(keyAxis));
}
registerListStateProvider(() => ({ scroll: { ...scrollPositions } }));

const RESTORE_MAX_MS = 10000;
const restoreWhenReachable = (ref, keyAxis, axis, target) => {
  const startedAt = Date.now();
  let frame = 0;
  const stop = () => {
    cancelAnimationFrame(frame);
    ["touchstart", "wheel", "keydown", "mousedown"].forEach((type) =>
      window.removeEventListener(type, stop, true)
    );
  };
  ["touchstart", "wheel", "keydown", "mousedown"].forEach((type) =>
    window.addEventListener(type, stop, { capture: true, passive: true })
  );
  const apply = () => {
    const el = ref.current;
    if (!el || Date.now() - startedAt > RESTORE_MAX_MS) return stop();
    el.style.scrollBehavior = "auto";
    if (axis === "x") el.scrollLeft = target;
    else el.scrollTop = target;
    el.style.scrollBehavior = "";
    // Keep the target while the content is still too short to reach it.
    scrollPositions[keyAxis] = String(target);
    const now = axis === "x" ? el.scrollLeft : el.scrollTop;
    if (Math.abs(now - target) <= 2) return stop();
    frame = requestAnimationFrame(apply);
  };
  apply();
  return stop;
};

/**
 * A custom hook that preserves and restores scroll position
 * @param {React.RefObject} ref - Reference to the scrollable element
 * @param {string} key - Unique identifier for storing scroll position
 * @param {"x" | "y"} axis - Which axis to preserve ("x" for horizontal, "y" for vertical)
 */
export const useScrollPreservation = (ref, key, axis = "x") => {
  const mountedAtRef = useRef(Date.now());
  const navAtRef = useRef(0);
  const location = useLocation();
  useEffect(() => { // reset on route change
    navAtRef.current = Date.now();
  }, [location.pathname, location.search]);

  // Cold back: restore before the first paint, then until reachable.
  useLayoutEffect(() => {
    const keyAxis = `${key}-${axis}`;
    if (!pendingRestore.has(keyAxis)) return undefined;
    pendingRestore.delete(keyAxis);
    const target = parseInt(scrollPositions[keyAxis], 10);
    if (!target) return undefined;
    return restoreWhenReachable(ref, keyAxis, axis, target);
  }, [key, axis, ref]);

  // Restore scroll position on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      const savedScroll = scrollPositions[`${key}-${axis}`];
      if (savedScroll && ref.current) {
        // Disable smooth scrolling temporarily
        ref.current.style.scrollBehavior = "auto";

        if (axis === "x") {
          ref.current.scrollLeft = parseInt(savedScroll, 10);
        } else {
          ref.current.scrollTop = parseInt(savedScroll, 10);
        }

        // Re-enable smooth scrolling
        requestAnimationFrame(() => {
          if (ref.current) {
            ref.current.style.scrollBehavior = "";
          }
        });
      } else if (ref.current && axis === "y") {
        ref.current.scrollTop = 0
      }
    });
  }, [key, axis, ref]);

  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      // as changing path triggers this which is not ideal
      // we ignore scrolls in the first 250ms after mount or nav
      const now = Date.now();
      if (now - mountedAtRef.current < 250) return;
      if (navAtRef.current && now - navAtRef.current < 250) return;

      const keyAxis = `${key}-${axis}`;
      if (!ref.current) return;
      if (axis === "x") {
        scrollPositions[keyAxis] = ref.current.scrollLeft.toString();
      } else {
        scrollPositions[keyAxis] = ref.current.scrollTop.toString();
      }
    };

    const element = ref.current;
    if (element) {
      element.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        element.removeEventListener("scroll", handleScroll);
      };
    }
  }, [key, axis, ref]);

  // New: allow setting scroll position manually
  const setScroll = useCallback(
    (value) => {
      if (!ref.current) return;
      if (axis === "x") {
        ref.current.scrollLeft = value;
        scrollPositions[`${key}-x`] = value.toString();
      } else {
        ref.current.scrollTop = value;
        scrollPositions[`${key}-y`] = value.toString();
      }
    },
    [ref, key, axis]
  );

  return { setScroll };
};
